import type {Vec3} from 'virtual-tour-viewer/src/modules/React360Proxy';
import {Euler, Vector3} from 'virtual-tour-viewer/src/modules/React360Proxy';

export default class CameraUtils {
    static rotateCamera(rotation, deltaYaw, deltaPitch) {
        // premultiply the camera rotation by the horizontal (pitch) rotation,
        // then multiply by the vertical (yaw) rotation

        const cp = Math.cos(deltaYaw / 2);
        const sp = Math.sin(deltaYaw / 2);
        const cy = Math.cos(deltaPitch / 2);
        const sy = Math.sin(deltaPitch / 2);

        const x1 = rotation[0];
        const y1 = rotation[1];
        const z1 = rotation[2];
        const w1 = rotation[3];

        const x2 = cp * x1 + sp * z1;
        const y2 = cp * y1 + sp * w1;
        const z2 = cp * z1 - sp * x1;
        const w2 = cp * w1 - sp * y1;

        const x3 = w2 * sy + x2 * cy;
        const y3 = y2 * cy + z2 * sy;
        const z3 = -y2 * sy + z2 * cy;
        const w3 = w2 * cy - x2 * sy;

        return [x3, y3, z3, w3];
    }

    static getEulerFromVec3(point: Vec3) {
        const magnitude = CameraUtils.getVectorMagnitude(point);
        const x = point[0] / magnitude;
        const y = point[1] / magnitude;
        const z = point[2] / magnitude;
        /**
         * point has been normalized, having a magnitude of 1.
         * Because of this, we can take arcsin(y/1) to get the angle
         * in radians from the horizontal plane to the point.
         *
         * Pitch and yaw are being calculated with respect to the -z axis as the default direction to look at.
         */
        const horizontalProjectionMagnitude = CameraUtils.getVectorMagnitude([x, 0, z]);
        const deltaPitch = Math.asin(y);
        const deltaYaw = -Math.sign(x) * Math.acos(-z / horizontalProjectionMagnitude);
        return new Euler(deltaPitch || 0, deltaYaw || 0, 0);
    }

    static getEulerFromVector3(point: Vector3) {
        return this.getEulerFromVec3(point.toArray());
    }

    static getVectorMagnitude([...coordinates]) {
        return Math.sqrt(
            [...coordinates].reduce((sum, currentValue) => sum + currentValue * currentValue, 0)
        );
    }
}
